import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  GlassCard,
  StatusPill,
} from '@jumpstone/ui-react';
import { format_date } from '../../../lib/format';
import { COMPENSATION_REASON_KEY } from '../../../lib/label_keys';
import type {
  CompensationMutationFailure,
  UseEmployeeCompensationResult,
} from '../../../data/use_employee_compensation';
import { CompensationFailureAlert } from '../compensation_failure_alert';
import { todayIsoUtc } from '../employee_form_compensation_utils';
import { format_pay_amount } from '../pay_amount_display';
import {
  COMPENSATION_STATUS_KEY,
  COMPENSATION_STATUS_PILL,
  buildSalaryHistoryView,
  rowAmountCents,
  type SalaryHistoryViewRow,
} from './salary_history_utils';

export interface SalaryHistoryCardProps {
  /** The profile screen's single `useEmployeeCompensation` instance (so a change made elsewhere refreshes this card). */
  compensation: UseEmployeeCompensationResult;
  /** The organisation base currency: rows with no pay currency of their own are denominated in it. */
  currency: string;
  /** Injectable "today" (ISO date) for deterministic tests; defaults to the current UTC date. */
  today?: string;
}

/**
 * The employee's effective-dated salary history: scheduled, current, past and
 * (with a muted status) cancelled / superseded entries. Always rendered -- an
 * employee that predates salary history, or was never edited, gets an empty
 * state saying the current pay is the only value on record. Future-dated
 * entries can be cancelled here; an entry an approved pay run already used is
 * rejected server-side and explained in the confirmation dialog.
 */
export function SalaryHistoryCard({
  compensation,
  currency,
  today = todayIsoUtc(),
}: SalaryHistoryCardProps): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { state, rows, retry, cancel_change } = compensation;
  const [cancelling, setCancelling] = useState<SalaryHistoryViewRow | null>(
    null,
  );
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelFailure, setCancelFailure] =
    useState<CompensationMutationFailure | null>(null);

  const view = buildSalaryHistoryView(rows, today);

  function closeCancel(): void {
    setCancelling(null);
    setCancelLoading(false);
    setCancelFailure(null);
  }

  return (
    <GlassCard className="rounded-2xl p-5 lg:col-span-2">
      <h2 className="text-[14.5px] font-bold mb-1.5">
        {t('employees.salary_history')}
      </h2>

      {state.status === 'loading' && (
        <div className="nv-skel" style={{ height: 48 }} />
      )}

      {state.status === 'entitlement_required' && (
        <EmptyState
          title={t('data_centre.entitlement_required_title')}
          description={t('data_centre.entitlement_required_description')}
        />
      )}

      {state.status === 'error' && (
        <EmptyState
          title={t('employees.compensation_load_error')}
          action={
            <Button variant="secondary" size="sm" onClick={retry}>
              {t('employees.compensation_retry')}
            </Button>
          }
        />
      )}

      {(state.status === 'ready' || state.status === 'no_entity') &&
        view.length === 0 && (
          <EmptyState
            title={t('employees.compensation_no_history')}
            description={t('employees.compensation_no_history_description')}
          />
        )}

      {state.status === 'ready' && view.length > 0 && (
        <div className="overflow-x-auto">
          <table className="nv-table w-full text-[13px]">
            <thead>
              <tr>
                <th scope="col">
                  {t('employees.compensation_col_effective_from')}
                </th>
                <th scope="col">{t('employees.form_pay_basis')}</th>
                <th scope="col" className="text-end">
                  {t('employees.compensation_col_amount')}
                </th>
                <th scope="col">{t('employees.compensation_reason')}</th>
                <th scope="col">{t('employees.column_status')}</th>
                {cancel_change !== undefined && (
                  <th scope="col">
                    <span className="sr-only">{t('common.actions')}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {view.map((entry) => (
                <tr key={entry.row.id}>
                  <td>{format_date(entry.row.effective_from, locale)}</td>
                  <td>
                    {entry.row.pay_basis === 'hourly'
                      ? t('employees.form_pay_basis_hourly')
                      : t('employees.form_pay_basis_salaried')}
                  </td>
                  <td className="text-end num">
                    {format_pay_amount(
                      rowAmountCents(entry.row),
                      entry.row.pay_currency_code ?? currency,
                      locale,
                    )}
                  </td>
                  <td>
                    {t(COMPENSATION_REASON_KEY[entry.row.reason])}
                    {entry.row.note !== null && (
                      <span className="block text-[12px] text-[color:var(--ink-muted)]">
                        {entry.row.note}
                      </span>
                    )}
                  </td>
                  <td>
                    <StatusPill
                      status={COMPENSATION_STATUS_PILL[entry.status]}
                      label={t(COMPENSATION_STATUS_KEY[entry.status])}
                    />
                  </td>
                  {cancel_change !== undefined && (
                    <td className="text-end">
                      {entry.can_cancel && (
                        <Button
                          variant="ghost-danger"
                          size="sm"
                          onClick={() => {
                            setCancelling(entry);
                            setCancelFailure(null);
                          }}
                        >
                          {t('employees.compensation_cancel')}
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={cancelling !== null}
        onOpenChange={(open) => {
          if (!open) closeCancel();
        }}
        title={t('employees.compensation_cancel')}
        description={
          cancelling !== null
            ? t('employees.compensation_cancel_confirm', {
                date: format_date(cancelling.row.effective_from, locale),
              })
            : ''
        }
        confirmLabel={t('employees.compensation_cancel')}
        cancelLabel={t('employees.compensation_cancel_keep')}
        variant="destructive"
        loading={cancelLoading}
        onConfirm={() => {
          if (cancelling === null || cancel_change === undefined) return;
          setCancelLoading(true);
          setCancelFailure(null);
          void cancel_change(cancelling.row.id).then((result) => {
            setCancelLoading(false);
            if (result.outcome === 'success') {
              closeCancel();
            } else {
              setCancelFailure(result.failure);
            }
          });
        }}
      >
        {cancelFailure?.kind === 'error' && (
          <p role="alert" className="nv-help is-error">
            {t('employees.compensation_cancel_error')}
          </p>
        )}
        {cancelFailure !== null && cancelFailure.kind !== 'error' && (
          <CompensationFailureAlert failure={cancelFailure} t={t} />
        )}
      </ConfirmDialog>
    </GlassCard>
  );
}
